import { COUNTRY_DIAL_CODES, COUNTRY_LABELS } from "./auth.js";
/**
 * ═══════════════════════════════════════════════════════════
 * EL PAÍS ES LA LLAVE MAESTRA (Anahí, 1-sep)
 *
 * «Que elija el país, y dependiendo el país se configure el idioma […]
 * si eligen México, se escoge el español y las APIs de Stripe de México;
 * si se elige Estados Unidos, las APIs de Stripe de Estados Unidos y el
 * idioma inglés. Es la única forma de cambiar el idioma y el país, para
 * evitar errores de pagos.»
 *
 * ── Qué problema resuelve, y por qué se quitó el selector ────────────
 * Antes había DOS mandos independientes: el idioma se elegía en una
 * píldora del navbar (detectado del navegador) y la moneda estaba fija en
 * MXN para todo el mundo. Eso permite el estado que nadie quiere: alguien
 * viendo el sitio en inglés, con precios en pesos, cobrando por la cuenta
 * de Stripe de México. Tres decisiones que TIENEN que ir juntas viajando
 * por separado es una factura equivocada esperando a ocurrir.
 *
 * Aquí el país decide las tres, y por eso el selector del navbar se
 * retiró: dos maneras de cambiar el idioma son dos maneras de
 * desincronizarlo del cobro.
 *
 * ── Por qué solo MX y US ─────────────────────────────────────────────
 * Son los países donde HAY cuenta de Stripe. Un tercer país sin cuenta
 * no es una opción a medias: es un cobro que no se puede hacer. `OTHER`
 * sobrevive como valor GUARDADO —las cuentas que entraron con Google
 * nacieron así, porque Google no entrega el país— pero no se puede
 * elegir: esas cuentas tienen que decidir, y hasta que decidan se les
 * trata como México, que es la casa.
 * ═══════════════════════════════════════════════════════════
 */
/** Los idiomas del sistema. Uno por país donde se opera. */
export const LANGUAGES = ["es", "en"];
/**
 * Los países ELEGIBLES: donde hay cuenta de Stripe y, por tanto, donde se
 * puede cobrar. No incluye `OTHER` a propósito — ver arriba.
 */
export const OPERATING_COUNTRIES = ["MX", "US"];
export const COUNTRY_SETTINGS = {
    MX: {
        language: "es",
        currency: "MXN",
        stripeAccount: "MXN",
        dialCode: COUNTRY_DIAL_CODES.MX,
        label: COUNTRY_LABELS.MX,
        locale: "es-MX",
    },
    US: {
        language: "en",
        currency: "USD",
        stripeAccount: "USD",
        dialCode: COUNTRY_DIAL_CODES.US,
        label: COUNTRY_LABELS.US,
        locale: "en-US",
    },
};
/**
 * El país de la casa. Es a lo que cae todo lo que no ha elegido: quien
 * navega sin sesión, y las cuentas de Google que nacieron con `OTHER`.
 *
 * Que el respaldo sea México y no "lo que diga el navegador" es la
 * decisión de fondo: un visitante con el navegador en inglés vería
 * precios en dólares que su cuenta no puede pagar todavía. El sistema
 * enseña la casa hasta que alguien diga otra cosa.
 */
export const HOUSE_COUNTRY = "MX";
/** Las opciones del selector de país, en el orden en que se enseñan. */
export const COUNTRY_OPTIONS = OPERATING_COUNTRIES.map((code) => ({
    value: code,
    label: COUNTRY_SETTINGS[code].label,
}));
export function isOperatingCountry(value) {
    return OPERATING_COUNTRIES.includes(value);
}
/**
 * La configuración de un país guardado, sea el que sea.
 *
 * Nunca devuelve `undefined`: un país desconocido —`OTHER`, o uno que
 * alguien dejó en la base antes de esta regla— cae a la casa. Una pantalla
 * que tiene que preguntar "¿y si no hay configuración?" acaba con un
 * `?? "MXN"` distinto en cada archivo.
 */
export function settingsFor(country) {
    return isOperatingCountry(country)
        ? COUNTRY_SETTINGS[country]
        : COUNTRY_SETTINGS[HOUSE_COUNTRY];
}
/** El idioma que le toca a un país. */
export function languageFor(country) {
    return settingsFor(country).language;
}
/** La moneda en la que se le cobra a un país. */
export function currencyFor(country) {
    return settingsFor(country).currency;
}
//# sourceMappingURL=country.js.map